import axios from 'axios';
import {
  AddressDto,
  AddressSuggestionDto,
  AsyncOrderResponseDto,
  BannerDto,
  CatalogPromotionDto,
  CashbackConfig,
  Category,
  CategoryDto,
  CheckoutValidationResponseDto,
  CouponValidationRequestDto,
  CouponValidationResponseDto,
  CreateOrderRequestDto,
  CustomerDto,
  CustomerLoginRequestDto,
  CustomerLoginResponseDto,
  CustomerRefreshRequestDto,
  CustomerRegisterRequestDto,
  CustomerRegisterResponseDto,
  DeliveryInfoDto,
  DeliveryMethod,
  ImageDto,
  MenuItem,
  MerchantDeliveryOptionsDto,
  MerchantDomainsResponseDto,
  MerchantDto,
  MerchantOpeningHourDto,
  MerchantThemeDto,
  Order,
  OrderDto,
  PageOrderDto,
  PageWalletTransactionDto,
  PaymentMethod,
  PaymentMethodDto,
  ProductDto,
  Restaurant,
  Review,
  ReviewInvitationPublicDto,
  ReviewDto,
  SubmitOrderReviewRequestDto,
  UpdateUserProfileRequestDto,
  SalesRecoveryOpportunityCaptureRequestDto,
  SalesRecoveryOpportunityDto,
  WalletBalanceDto
} from '../types';

// Interface for ZIP code API response
interface ZipCodeResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  ibge: string;
  gia: string;
  ddd: string;
  siafi: string;
  erro?: boolean;
}

// Timeout padrão das requisições. Sem isso o axios usa 0 (sem timeout), então
// uma requisição pendurada (ex.: criação de pedido) nunca abortava nem dava
// feedback de erro. O POST /v1/orders é assíncrono (responde 202 rápido), logo
// 20s é folgado e serve como corte de segurança para qualquer chamada.
const DEFAULT_TIMEOUT_MS = 20000;

// Create axios instances for each microservice
const customerApiInstance = axios.create({
  baseURL: process.env.REACT_APP_CUSTOMER_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8082/app/customer' : ''),
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

const merchantApiInstance = axios.create({
  baseURL: process.env.REACT_APP_MERCHANT_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8083/app/merchant' : ''),
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

const paymentApiInstance = axios.create({
  baseURL: process.env.REACT_APP_PAYMENTS_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8084/app/payments' : ''),
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Sessão / refresh de token
// ---------------------------------------------------------------------------
// Quando o access token expira, o backend responde 401. Nesse momento tentamos
// renovar a sessão silenciosamente com o refresh token (POST /v1/auth/refresh).
// - Se o refresh funcionar: persistimos os novos tokens e reexecutamos a
//   requisição original (e as que ficaram na fila enquanto renovávamos).
// - Se o refresh falhar (refresh token inválido/expirado): limpamos a sessão e
//   disparamos o evento `unauthorized-error`, que o AuthContext usa para abrir
//   o modal de login.
//
// CAVEAT (backend): hoje o endpoint /v1/auth/refresh NÃO está em
// `permit-all-paths` e possui @PreAuthorize("hasAuthority('ROLE_CUSTOMER')").
// O refresh token carrega a role ROLE_CUSTOMER_REFRESH (não ROLE_CUSTOMER),
// então, com o access token expirado, a própria chamada de refresh recebe
// 401/403 até que o backend libere esse endpoint. Este código já está pronto
// para funcionar assim que o backend permitir o refresh sem o access token
// válido; enquanto isso, o fluxo cai no fallback do modal de login.

const AUTH_PATHS_NO_REFRESH = [
  '/v1/auth/login',
  '/v1/auth/refresh',
  '/v1/auth/register',
  '/v1/auth/first-access',
  '/v1/auth/forgot-password',
  '/v1/auth/reset-password',
];

const isAuthPath = (url: string = ''): boolean =>
    AUTH_PATHS_NO_REFRESH.some(path => url.includes(path));

const clearSession = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
};

const handleSessionExpired = () => {
  clearSession();
  // AuthContext escuta este evento para limpar o usuário e abrir o login.
  window.dispatchEvent(new CustomEvent('unauthorized-error'));
};

// Controle de concorrência: só uma renovação por vez; as demais requisições
// aguardam na fila e são liberadas com o novo token (ou rejeitadas na falha).
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

const subscribeTokenRefresh = (cb: (token: string | null) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string | null) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const performTokenRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    // Usamos um axios "cru" (sem os interceptors) para evitar recursão em 401.
    // O refresh token vai no body (contrato CustomerRefreshRequestDto) e também
    // no header Authorization — assim o fluxo passa a funcionar automaticamente
    // quando o backend liberar o endpoint (ver CAVEAT acima).
    const response = await axios.post<CustomerLoginResponseDto>(
        `${customerApiInstance.defaults.baseURL}/v1/auth/refresh`,
        { refreshToken },
        {
          timeout: DEFAULT_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
          },
        }
    );

    const data = response.data;
    const userWithFullname = {
      ...data.user,
      name: `${data.user.firstName} ${data.user.lastName}`.trim(),
    };

    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(userWithFullname));

    // Mantém o AuthContext em sincronia com os novos dados/sessão.
    window.dispatchEvent(new CustomEvent('token-refreshed', { detail: userWithFullname }));

    return data.accessToken;
  } catch (error) {
    return null;
  }
};

// Helper to add auth interceptor (com refresh automático) a uma instância
const addAuthInterceptor = (instance: any) => {
  instance.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
  );

  instance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Só tentamos refresh em 401, uma única vez por requisição, e nunca
        // para os próprios endpoints de autenticação.
        if (
            status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !isAuthPath(originalRequest.url)
        ) {
          // Sem refresh token não há como renovar — sessão expirada.
          if (!localStorage.getItem('refreshToken')) {
            handleSessionExpired();
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // Já existe uma renovação em andamento: entra na fila.
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              subscribeTokenRefresh((newToken) => {
                if (newToken) {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  resolve(instance(originalRequest));
                } else {
                  reject(error);
                }
              });
            });
          }

          isRefreshing = true;
          const newToken = await performTokenRefresh();
          isRefreshing = false;
          onTokenRefreshed(newToken);

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance(originalRequest);
          }

          // Refresh falhou: encerra a sessão e sinaliza o modal de login.
          handleSessionExpired();
        }

        return Promise.reject(error);
      }
  );
};

// Add interceptors to all instances
addAuthInterceptor(customerApiInstance);
addAuthInterceptor(merchantApiInstance);
addAuthInterceptor(paymentApiInstance);

// Keep the default api instance for compatibility if needed, but point to a default
const api = merchantApiInstance;

// Mappers to convert API DTOs to Frontend types
const sortPromotions = (promos: CatalogPromotionDto[] = []) => [...promos].sort((a, b) => {
  const priorityA = a.priority ?? 999999;
  const priorityB = b.priority ?? 999999;
  if (priorityA !== priorityB) return priorityA - priorityB;
  return a.name.localeCompare(b.name);
});

const mapMerchantToRestaurant = (dto: MerchantDto): Restaurant => ({
  id: dto.id,
  name: dto.name,
  slug: dto.slug,
  enabled: dto.enabled,
  isOpen: dto.isOpen,
  description: dto.description,
  logo: dto.logoUrl,
  coverImage: dto.coverUrl,
  address: dto.address,
  deliveryInfo: dto.deliveryInfo,
  contact: {
    phone: dto.contactInfo?.phone || '',
    email: dto.contactInfo?.email,
    website: dto.contactInfo?.website,
  },
  openingHours: dto.openingHours,
  theme: dto.theme,
  cashbackConfig: dto.cashbackConfig,
  paymentMethods: dto.paymentMethods,
  deliveryOptions: dto.deliveryOptions,
  restaurantCategories: dto.restaurantCategories,
  tags: dto.tags,
  categories: dto.categories
    ?.map(cat => ({
      ...cat,
      promos: sortPromotions(cat.promos || []),
      products: cat.products?.sort((pA, pB) => {
        const seqA = pA.sequence ?? 999999;
        const seqB = pB.sequence ?? 999999;
        if (seqA !== seqB) return seqA - seqB;
        return pA.name.localeCompare(pB.name);
      }).map(p => ({
        ...p,
        categoryId: cat.id,
        modifiers: p.modifiers?.sort((mA, mB) => {
          const sA = mA.sequence ?? 999999;
          const sB = mB.sequence ?? 999999;
          if (sA !== sB) return sA - sB;
          return mA.name.localeCompare(mB.name);
        }).map(m => ({
          ...m,
          options: m.options?.sort((oA, oB) => {
            const sA = oA.sequence ?? 999999;
            const sB = oB.sequence ?? 999999;
            if (sA !== sB) return sA - sB;
            return oA.name.localeCompare(oB.name);
          }) || []
        })) || []
      })) || []
    })),
  stockControlEnabled: dto.stockControlEnabled,
  schedulingEnabled: dto.schedulingEnabled,
  autoAcceptOrders: dto.autoAcceptOrders,
  hasConnectedPos: dto.hasConnectedPos,
  pauses: dto.pauses,
  temporaryClosures: dto.temporaryClosures,
  banners: dto.banners,
  rating: dto.rating,
  reviewCount: dto.reviewCount,
});

const mapProductToMenuItem = (dto: ProductDto, categoryId?: string): MenuItem => ({
  id: dto.id,
  categoryId,
  name: dto.name,
  description: dto.description,
  price: dto.price,
  image: dto.imageUrl,
  available: dto.available,
  status: dto.status,
  quantity: dto.quantity,
  inventory: dto.inventory,
  dietaryRestriction: dto.dietaryRestriction,
  availability: dto.availability,
  nutritionalInfo: dto.nutritionalInfo,
  productType: dto.productType,
  pdvCode: dto.pdvCode,
  sku: dto.sku,
  serving: dto.serving,
  comercial: dto.comercial,
  drinkInfo: dto.drinkInfo,
  operational: dto.operational,
  fiscal: dto.fiscal,
  subProducts: dto.subProducts,
  sequence: dto.sequence,
  extras: dto.modifiers
    ?.sort((a, b) => {
      const seqA = a.sequence ?? 999999;
      const seqB = b.sequence ?? 999999;
      if (seqA !== seqB) return seqA - seqB;
      return a.name.localeCompare(b.name);
    })
    .map(mod => ({
      id: mod.id,
      name: mod.name,
      description: mod.description,
      maxSelections: mod.maxQuantity,
      required: mod.required,
      sequence: mod.sequence,
      options: mod.options
        ?.sort((a, b) => {
          const seqA = a.sequence ?? 999999;
          const seqB = b.sequence ?? 999999;
          if (seqA !== seqB) return seqA - seqB;
          return a.name.localeCompare(b.name);
        })
        .map(opt => ({
          id: opt.id,
          name: opt.name,
          description: opt.description,
          price: opt.price,
          imageUrl: opt.imageUrl,
          available: opt.available,
          status: opt.status,
          sequence: opt.sequence,
        })) || [],
    })),
});

const mapCategoryToCategory = (dto: CategoryDto): Category => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  image: dto.imageUrl,
  template: dto.template,
  sequence: dto.sequence,
  promos: sortPromotions(dto.promos || []),
  items: dto.products
    ?.sort((pA, pB) => {
      const seqA = pA.sequence ?? 999999;
      const seqB = pB.sequence ?? 999999;
      if (seqA !== seqB) return seqA - seqB;
      return pA.name.localeCompare(pB.name);
    })
    .map(p => mapProductToMenuItem(p, dto.id)) || [],
});

const mapReviewToReview = (dto: ReviewDto): Review => ({
  id: dto.id,
  userName: dto.customerName,
  rating: dto.rating,
  comment: dto.comment,
  date: dto.createdAt,
});

// Restaurant API
export const restaurantApi = {
  // Get restaurant information
  getRestaurant: async (restaurantId: string): Promise<Restaurant> => {
    const response = await merchantApiInstance.get<MerchantDto>(`/v1/${restaurantId}`);
    return mapMerchantToRestaurant(response.data);
  },

  // Get merchant ID by domain
  getMerchantByDomain: async (domain: string): Promise<MerchantDomainsResponseDto> => {
    const response = await merchantApiInstance.get<MerchantDomainsResponseDto>(`/v1/domain?domain=${domain}`);
    return response.data;
  },

  // Get merchant ID by slug
  getMerchantBySlug: async (slug: string): Promise<MerchantDomainsResponseDto> => {
    const response = await merchantApiInstance.get<MerchantDomainsResponseDto>(`/v1/slug?slug=${slug}`);
    return response.data;
  },

  // Get all categories with menu items
  getMenu: async (restaurantId: string): Promise<Category[]> => {
    const response = await merchantApiInstance.get<CategoryDto[]>(`/v1/${restaurantId}/catalog`);
    return response.data
      .map(mapCategoryToCategory);
  },

  // Get restaurant banners
  getBanners: async (restaurantId: string): Promise<BannerDto[]> => {
    const response = await merchantApiInstance.get<BannerDto[]>(`/v1/${restaurantId}/banners`);
    return response.data;
  },

  // Get restaurant opening hours
  getOpeningHours: async (restaurantId: string): Promise<MerchantOpeningHourDto[]> => {
    const response = await merchantApiInstance.get<MerchantOpeningHourDto[]>(`/v1/${restaurantId}/opening-hours`);
    return response.data;
  },

  // Get restaurant payment methods
  getPaymentMethods: async (restaurantId: string): Promise<PaymentMethodDto[]> => {
    // Note: The endpoint in openapi is /v1/{merchantId}/payment-methods
    const response = await merchantApiInstance.get<PaymentMethodDto[]>(`/v1/${restaurantId}/payment-methods`);
    return response.data;
  },

  // Get merchant settings (delivery options)
  getSettings: async (restaurantId: string): Promise<MerchantDeliveryOptionsDto> => {
    const response = await merchantApiInstance.get<MerchantDeliveryOptionsDto>(`/v1/${restaurantId}/settings`);
    return response.data;
  },

  // Get merchant theme
  getTheme: async (restaurantId: string): Promise<MerchantThemeDto> => {
    const response = await merchantApiInstance.get<MerchantThemeDto>(`/v1/${restaurantId}/theme`);
    return response.data;
  },

  // Get merchant cashback configuration
  getCashbackConfig: async (restaurantId: string): Promise<CashbackConfig> => {
    const response = await merchantApiInstance.get<CashbackConfig>(`/v1/${restaurantId}/cashback`);
    return response.data;
  },

  // Get restaurant reviews
  getReviews: async (restaurantId: string): Promise<Review[]> => {
    const response = await merchantApiInstance.get<ReviewDto[]>(`/v1/${restaurantId}/reviews`);
    return response.data.map(mapReviewToReview);
  },

  // Get a specific category (if supported by catalog, otherwise filter)
  getCategory: async (restaurantId: string, categoryId: string): Promise<Category> => {
    const categories = await restaurantApi.getMenu(restaurantId);
    const category = categories.find(c => c.id === categoryId);
    if (!category) throw new Error('Category not found');
    return category;
  },

  // Get a specific menu item
  getMenuItem: async (restaurantId: string, menuItemId: string): Promise<MenuItem> => {
    const categories = await restaurantApi.getMenu(restaurantId);
    for (const category of categories) {
      const item = category.items.find(i => i.id === menuItemId);
      if (item) return item;
    }
    throw new Error('Menu item not found');
  },

  // Search menu items
  searchMenuItems: async (restaurantId: string, query: string): Promise<MenuItem[]> => {
    const categories = await restaurantApi.getMenu(restaurantId);
    const allItems = categories.flatMap(c => c.items);
    return allItems.filter(item =>
        item.name.toLowerCase().includes(query.toLowerCase()) ||
        item.description?.toLowerCase().includes(query.toLowerCase())
    );
  },

  // Validate coupon
  validateCoupon: async (merchantId: string, request: CouponValidationRequestDto): Promise<CouponValidationResponseDto> => {
    const response = await merchantApiInstance.post<CouponValidationResponseDto>(`/v1/${merchantId}/coupons/validate`, request);
    return response.data;
  },

  // Check if restaurant is available (no cache)
  isAvailable: async (merchantId: string): Promise<boolean> => {
    const response = await merchantApiInstance.get<boolean>(`/v1/${merchantId}/availability`);
    return response.data;
  },
};

// Rastreio público da entrega (por token do link). Léo GO #305.
export interface DeliveryTrackingResponse {
  orderDisplayId?: string | null;
  status: string;
  step: string;
  stepLabel: string;
  driverFirstName?: string | null;
  etaMinutes?: number | null;
  confirmationCode?: string | null;
  deliveredAt?: string | null;
  driverPosition?: { latitude: number; longitude: number } | null;
  destinationLatitude?: number | null;
  destinationLongitude?: number | null;
}

export const deliveryTrackingApi = {
  track: async (token: string): Promise<DeliveryTrackingResponse> => {
    const response = await merchantApiInstance.get<DeliveryTrackingResponse>(`/v1/track/${token}`);
    return response.data;
  },
};

// Customer Auth API
export const customerAuthApi = {
  login: async (credentials: CustomerLoginRequestDto): Promise<CustomerLoginResponseDto> => {
    const response = await customerApiInstance.post<CustomerLoginResponseDto>('/v1/auth/login', credentials);
    return response.data;
  },

  register: async (data: CustomerRegisterRequestDto): Promise<CustomerRegisterResponseDto> => {
    const response = await customerApiInstance.post<CustomerRegisterResponseDto>('/v1/auth/register', data);
    return response.data;
  },

  refresh: async (data: CustomerRefreshRequestDto): Promise<CustomerLoginResponseDto> => {
    const response = await customerApiInstance.post<CustomerLoginResponseDto>('/v1/auth/refresh', data);
    return response.data;
  },

  logout: async (): Promise<void> => {
    await customerApiInstance.post('/v1/auth/logout');
  },

  firstAccessStart: async (data: { cpfOrPhone: string; merchantId?: string }): Promise<void> => {
    await customerApiInstance.post('/v1/auth/first-access/start', data);
  },

  firstAccessConfirm: async (data: { cpfOrPhone: string; code: string; password: string }): Promise<CustomerLoginResponseDto> => {
    const response = await customerApiInstance.post<CustomerLoginResponseDto>('/v1/auth/first-access/confirm', data);
    return response.data;
  },

  forgotPassword: async (data: { cpfOrPhone: string; merchantId?: string }): Promise<void> => {
    await customerApiInstance.post('/v1/auth/forgot-password', data);
  },

  resetPassword: async (data: { cpfOrPhone: string; code: string; password: string }): Promise<CustomerLoginResponseDto> => {
    const response = await customerApiInstance.post<CustomerLoginResponseDto>('/v1/auth/reset-password', data);
    return response.data;
  },
};

// Customer API
export const customerApi = {
  getProfile: async (): Promise<CustomerDto> => {
    const response = await customerApiInstance.get<CustomerDto>('/v1/auth/me');
    // Map the response to CustomerDto if needed, CustomerLoginResponseDto has user field
    return (response.data as any).user;
  },

  updateProfile: async (data: UpdateUserProfileRequestDto): Promise<CustomerDto> => {
    const response = await customerApiInstance.put<CustomerDto>('/v1', data);
    return response.data;
  },

  updateImage: async (image: ImageDto): Promise<{ [key: string]: string }> => {
    const response = await customerApiInstance.post<{ [key: string]: string }>('/v1/image', image);
    return response.data;
  },

  deleteAccount: async (): Promise<void> => {
    await customerApiInstance.delete('/v1');
  },

  listAddresses: async (): Promise<AddressDto[]> => {
    const response = await customerApiInstance.get<AddressDto[]>('/v1/addresses');
    return response.data;
  },

  addAddress: async (address: AddressDto): Promise<AddressDto> => {
    const response = await customerApiInstance.post<AddressDto>('/v1/addresses', address);
    return response.data;
  },

  updateAddress: async (addressId: string, address: AddressDto): Promise<AddressDto> => {
    const response = await customerApiInstance.put<AddressDto>(`/v1/addresses/${addressId}`, address);
    return response.data;
  },

  deleteAddress: async (addressId: string): Promise<void> => {
    await customerApiInstance.delete(`/v1/addresses/${addressId}`);
  },

  getCoordinates: async (address: AddressDto): Promise<{ [key: string]: number }> => {
    const response = await customerApiInstance.post<{ [key: string]: number }>('/v1/address/geocode', address);
    return response.data;
  },

  // Autocomplete de endereço (Google Places via backend)
  autocompleteAddress: async (query: string): Promise<AddressSuggestionDto[]> => {
    const response = await customerApiInstance.get<AddressSuggestionDto[]>('/v1/address/autocomplete', {
      params: { query }
    });
    return response.data;
  },

  // Resolve um placeId em um endereço completo (com coordenadas)
  getAddressByPlaceId: async (placeId: string): Promise<AddressDto> => {
    const response = await customerApiInstance.get<AddressDto>(`/v1/address/place/${encodeURIComponent(placeId)}`);
    return response.data;
  },

  // Get customer wallet balance
  getWalletBalance: async (merchantId: string): Promise<WalletBalanceDto> => {
    const response = await paymentApiInstance.get<WalletBalanceDto>('/v1/wallet/balance', {
      headers: {
        'X-Merchant-Id': merchantId
      }
    });
    return response.data;
  },

  // Get customer wallet transactions
  getWalletTransactions: async (merchantId: string, page = 0, size = 20): Promise<PageWalletTransactionDto> => {
    const response = await paymentApiInstance.get<PageWalletTransactionDto>('/v1/wallet/transactions', {
      headers: {
        'X-Merchant-Id': merchantId
      },
      params: {
        page,
        size
      }
    });
    return response.data;
  },
};

// Simple in-memory cache for delivery fee calculations
const deliveryFeeCache: Record<string, { data: DeliveryInfoDto; timestamp: number }> = {};
const CACHE_TTL = 30000; // 30 seconds

// Merchant API
export const merchantApi = {
  calculateDeliveryFee: async (merchantId: string, lat: number, lng: number): Promise<DeliveryInfoDto> => {
    const cacheKey = `${merchantId}-${lat.toFixed(6)}-${lng.toFixed(6)}`;
    const now = Date.now();
    
    if (deliveryFeeCache[cacheKey] && (now - deliveryFeeCache[cacheKey].timestamp) < CACHE_TTL) {
      return deliveryFeeCache[cacheKey].data;
    }

    const response = await merchantApiInstance.get<DeliveryInfoDto>(`/v1/${merchantId}/delivery-fee?lat=${lat}&lng=${lng}`);
    
    deliveryFeeCache[cacheKey] = {
      data: response.data,
      timestamp: now
    };

    return response.data;
  },

  listMerchants: async (): Promise<MerchantDto[]> => {
    const response = await merchantApiInstance.get<MerchantDto[]>('/v1');
    return response.data;
  },

  getMerchantById: async (merchantId: string): Promise<MerchantDto> => {
    const response = await merchantApiInstance.get<MerchantDto>(`/v1/${merchantId}`);
    return response.data;
  },

  listNearby: async (lat: number, lng: number): Promise<MerchantDto[]> => {
    const response = await merchantApiInstance.get<MerchantDto[]>(`/v1/nearby?lat=${lat}&lng=${lng}`);
    return response.data;
  },
};

// ZIP Code API
export const zipCodeApi = {
  // Get address by ZIP code
  getAddressByZipCode: async (zipCode: string): Promise<ZipCodeResponse> => {
    try {
      // Remove any non-numeric characters from the ZIP code
      const cleanZipCode = zipCode.replace(/\D/g, '');

      // Make request to ViaCEP API
      const response = await axios.get(`https://viacep.com.br/ws/${cleanZipCode}/json/`);

      // Check if the API returned an error
      if (response.data.erro) {
        throw new Error('ZIP code not found');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching address by ZIP code:', error);
      throw error;
    }
  }
};

// Helper to map frontend order data to API request DTO
const mapOrderDataToRequest = (restaurantId: string, order: any): CreateOrderRequestDto => {
  // Build payments array
  const payments: any[] = [];

  // Helper to check if a method is cash
  const isCashMethod = (method: any) =>
      method === 'CASH' || method === 'MONEY' || method === PaymentMethod.CASH;

  // Add cashback payment if used
  if (order.cashbackUsedAmount && Number(order.cashbackUsedAmount) > 0) {
    payments.push({
      method: 'CASHBACK',
      amount: Number(order.cashbackUsedAmount)
    });
  }

  if (order.isTwoPayments) {
    payments.push({
      method: order.paymentMethod,
      amount: Number(order.paymentAmount1),
      changeFor: isCashMethod(order.paymentMethod) && order.needsChange ? (Number(order.changeFor) || 0) : undefined
    });
    payments.push({
      method: order.paymentMethod2,
      amount: Number(order.paymentAmount2),
      changeFor: isCashMethod(order.paymentMethod2) && order.needsChange2 ? (Number(order.changeFor2) || 0) : undefined
    });
  } else {
    payments.push({
      method: order.paymentMethod,
      amount: Number(order.total),
      changeFor: isCashMethod(order.paymentMethod) && order.needsChange ? (Number(order.changeFor) || 0) : undefined
    });
  }

  return {
    merchantId: restaurantId,
    payments: payments.map(p => {
      const paymentItem: any = {
        method: p.method,
        amount: Number(p.amount)
      };
      if (p.changeFor !== undefined && p.changeFor > 0) {
        paymentItem.changeFor = Number(p.changeFor);
      }
      return paymentItem;
    }),
    originChannel: 'MENU_WEB',
    customer: {
      id: order.customer?.id,
      name: order.customer?.name || '',
      phone: order.customer?.phone || '',
      email: order.customer?.email
    },
    couponCode: order.coupon || null,
    type: (order.deliveryMethod === 'TAKEAWAY' || order.deliveryMethod === DeliveryMethod.TAKEAWAY) ? 'TAKEAWAY' : 'DELIVERY',
    deliveryAddress: order.address ? {
      ...order.address,
      latitude: order.address.latitude ? Number(order.address.latitude) : undefined,
      longitude: order.address.longitude ? Number(order.address.longitude) : undefined,
    } : ((order.deliveryMethod === 'TAKEAWAY' || order.deliveryMethod === DeliveryMethod.TAKEAWAY) && restaurantId ? {
      street: 'Retirada no Estabelecimento',
      number: 'SN',
      neighborhood: '-',
      city: '-',
      state: '-',
      zipCode: '00000000',
    } : {
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
    }),
    items: order.items.map((item: any) => {
      const perUnitModifierMap = new Map<string, { id: string; name: string; price: number; quantity: number }>();
      const itemType = item.type === 'COMBO' || item.productType === 'COMBO' ? 'COMBO' : 'PRODUCT';

      if (item.options) {
        item.options.forEach((opt: any) => {
          if (opt.options && Array.isArray(opt.options)) {
            opt.options.forEach((subOpt: any) => {
              const existing = perUnitModifierMap.get(subOpt.id);
              const addQty = Math.round(Number(subOpt.quantity || 1));
              if (existing) {
                existing.quantity += addQty;
              } else {
                perUnitModifierMap.set(subOpt.id, {
                  id: subOpt.id,
                  name: subOpt.name,
                  price: Number(subOpt.price),
                  quantity: addQty,
                });
              }
            });
          } else {
            const existing = perUnitModifierMap.get(opt.id);
            const addQty = Math.round(Number(opt.quantity || 1));
            if (existing) {
              existing.quantity += addQty;
            } else {
              perUnitModifierMap.set(opt.id, {
                id: opt.id,
                name: opt.name,
                price: Number(opt.price),
                quantity: addQty,
              });
            }
          }
        });
      }

      const perUnitModifiersTotal = Array.from(perUnitModifierMap.values())
          .reduce((sum, m) => sum + (Number(m.price) * Math.max(1, Math.round(Number(m.quantity || 1)))), 0);
      const itemQuantity = Math.max(1, Math.round(Number(item.quantity)));
      const baseUnitPrice = Math.max(0, Number(item.price) - perUnitModifiersTotal);

      const finalModifiers = Array.from(perUnitModifierMap.values()).map(m => ({
        id: m.id,
        name: m.name,
        price: Number(m.price),
        quantity: Math.max(1, Math.round(Number(m.quantity || 1))) * itemQuantity,
      }));

      return {
        id: item.menuItemId || item.id,
        name: item.name,
        price: baseUnitPrice,
        quantity: itemQuantity,
        type: itemType,
        notes: item.notes,
        modifiers: finalModifiers,
      };
    }),
    scheduledTo: order.scheduledTo,
    cartCode: order.cartCode
  };
};

const mapOrderDtoToOrder = (order: OrderDto): Order => ({
  id: order.id,
  merchantId: order.merchantId,
  orderDisplayId: order.orderDisplayId,
  items: order.items.map(item => ({
    menuItemId: item.productId,
    name: item.name,
    quantity: item.quantity,
    price: item.price,
    notes: item.notes,
    options: item.modifiers?.map((mod: any) => ({
      id: mod.id,
      name: mod.name,
      price: mod.price,
      quantity: mod.quantity,
      available: true
    })) as any
  })),
  status: order.status as any,
  createdAt: order.createdAt,
  updatedAt: order.createdAt,
  subtotal: order.subtotal,
  total: order.total,
  deliveryFee: order.deliveryFee,
  discount: order.discount,
  couponCode: order.couponCode,
  paymentMethod: order.paymentMethod as any,
  payments: order.payments,
  deliveryAddress: order.deliveryAddress,
  estimatedDeliveryTime: order.estimatedDeliveryTime,
  deliveryConfirmationCode: order.deliveryConfirmationCode,
  cashbackEarnedAmount: order.cashbackEarnedAmount,
  cashbackUsedAmount: order.cashbackUsedAmount,
  cashbackStatus: order.cashbackStatus,
  cancellationReason: order.cancellationReason,
  cancellationReasonDescription: order.cancellationReasonDescription,
  reviewInvitation: order.reviewInvitation,
});

// Order API
export const orderApi = {
  // Create a new order
  createOrder: async (restaurantId: string, order: any): Promise<AsyncOrderResponseDto> => {
    const orderRequest = mapOrderDataToRequest(restaurantId, order);
    const response = await paymentApiInstance.post<AsyncOrderResponseDto>('/v1/orders', orderRequest);
    return response.data;
  },

  // Validate checkout
  validateCheckout: async (restaurantId: string, order: any): Promise<CheckoutValidationResponseDto> => {
    const orderRequest = mapOrderDataToRequest(restaurantId, order);
    const response = await paymentApiInstance.post<CheckoutValidationResponseDto>('/v1/orders/validate', orderRequest);
    return response.data;
  },

  // Get order by ID
  getOrder: async (restaurantId: string, orderId: string): Promise<Order> => {
    const response = await paymentApiInstance.get<OrderDto>(`/v1/orders/${orderId}`);
    return mapOrderDtoToOrder(response.data);
  },

  // List user orders
  listOrders: async (page = 0, size = 20): Promise<PageOrderDto> => {
    const response = await paymentApiInstance.get<{
      totalPages: number;
      totalElements: number;
      size: number;
      content: OrderDto[];
      number: number;
      numberOfElements: number;
      first: boolean;
      last: boolean;
      empty: boolean;
    }>('/v1/orders', {
      params: {
        page,
        size
      }
    });

    return {
      ...response.data,
      content: response.data.content.map(mapOrderDtoToOrder)
    };
  },
};

export const salesRecoveryApi = {
  captureOpportunity: async (request: SalesRecoveryOpportunityCaptureRequestDto): Promise<SalesRecoveryOpportunityDto> => {
    const response = await paymentApiInstance.post<SalesRecoveryOpportunityDto>('/v1/sales-recovery/opportunities', request);
    return response.data;
  },
  updatePushToken: async (
    cartCode: string,
    request: { merchantId: string; pushToken: string; pushOptIn?: boolean }
  ): Promise<SalesRecoveryOpportunityDto> => {
    const response = await paymentApiInstance.post<SalesRecoveryOpportunityDto>(
      `/v1/sales-recovery/opportunities/${cartCode}/push-token`,
      request
    );
    return response.data;
  },
};

export const reviewApi = {
  getInvitation: async (token: string): Promise<ReviewInvitationPublicDto> => {
    const response = await merchantApiInstance.get<ReviewInvitationPublicDto>(`/v1/reviews/token/${token}`);
    return response.data;
  },
  submit: async (request: SubmitOrderReviewRequestDto): Promise<ReviewDto> => {
    const response = await merchantApiInstance.post<ReviewDto>('/v1/reviews', request);
    return response.data;
  },
};

// NOTE: `api` aponta para `merchantApiInstance`, que já recebeu o
// `addAuthInterceptor` (com injeção de token + refresh automático em 401).
// Não adicionamos interceptors duplicados aqui para evitar tratar o 401 duas
// vezes (o que dispararia o refresh/logout em duplicidade).

export default api;
